import styled from 'styled-components';
import CircularProgress from '@material-ui/core/CircularProgress';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import ReCAPTCHA from 'react-google-recaptcha';
import { TextInput, SelectInput } from '@trueprodigy/components';

const questionOptions = [
  {
    label: 'For',
    value: '100'
  },
  {
    label: 'Against',
    value: '010'
  },
  {
    label: 'Neutral',
    value: '001'
  }
];

const styles = theme => ({
  dialogPaper: {
    overflow: 'inherit',
    width: '95vw',
    maxWidth: 800
  },
  dialogActions: {
    justifyContent: 'center',
    flexWrap: 'wrap',
    maxHeight: 100,
    '&> div': {
      maxHeight: 100
    }
  },
  button: {
    background: '#3d3f72',
    color: '#fff',
    '&:disabled': {
      background: '#ddd',
      color: '#3d3f7255'
    },
    '&:hover': {
      background: '#3d3f72DD',
      color: '#fff'
    },
    marginTop: 10,
    marginBottom: 10
  }
});

const FeedbackPanel = ({
  captchaKey,
  classes,
  handleSubmit,
  fetchTaxingUnitPollQuestions,
  unitId,
  year,
  pushToast
}) => {
  const [ comment, setComment ] = useState('');
  const [ isOwner, setIsOwner ] = useState('');
  const [ captchaOk, setCaptchaOk ] = useState(false);
  const [ questions, setQuestions ] = useState([]);
  const [ isLoadQuestion, setLoadedQuestion ] = useState(null);
  const [ responses, setResponses ] = useState({});
  const [ email, setEmail ] = useState('');
  const [ phone, setPhone ] = useState('');
  const isDisabled =
    !captchaOk || isOwner === '' || !comment || !email || !phone;

  const fetchPollQuestions = async () => {
    try {
      if (unitId) {
        setLoadedQuestion(true);
        const res = await fetchTaxingUnitPollQuestions({ id: unitId, year });
        setLoadedQuestion(false);
        if (res) {
          setQuestions(res.results);
        } else {
          setQuestions([]);
        }
      }
    } catch (e) {
      pushToast({ error: e, entity: 'Fetching Feedback Questions' });
    }
  };

  useEffect(() => {
    fetchPollQuestions();
  }, [ unitId, year ]);

  return (
    <div>
      <FeedbackPanel.Title>
        Provide Feedback to Your Taxing Authority
      </FeedbackPanel.Title>
      <form
        onSubmit={async ev => {
          ev.preventDefault();
          try {
            const mapedQuestions = Object.entries(responses).reduce(
              (acc, [ questionID, value ]) => {
                acc.push({
                  questionID,
                  response: {
                    responseFor: Number(value[0]),
                    responseAgainst: Number(value[1]),
                    responseNeutral: Number(value[2])
                  }
                });

                return acc;
              },
              []
            );

            handleSubmit({
              message: comment,
              isOwner,
              contactPhone: phone,
              contactEmail: email,
              questions: mapedQuestions
            });
          } catch (e) {
            pushToast({ error: e, entity: 'Sending Feedback Questions' });
          }
        }}
      >
        <div style={{ background: '#e2e2ff', padding: '10px 20px' }}>
          <Typography gutterBottom>
            As part of the new Texas tax transparency laws, we are providing
            the ability for taxpayers to submit comments to their taxing
            units. Take advantage of this by providing input in regards to
            adopted budgets, hearings, tax rates, and any other topic
            regarding property taxes in your area.
          </Typography>
          <Typography gutterBottom>
            We ask that all comments remain professional and courteous. Upon
            submission, your comment will be forwarded to taxing unit
            representatives.
          </Typography>
        </div>
        <div>
          <FeedbackPanel.Title>
            Comment
          </FeedbackPanel.Title>
          <FeedbackPanel.InputWrapper>
            <TextInput
              input={{
                onBlur: () => {},
                onChange: value => setComment(value),
                value: comment
              }}
              label='Type your comment here...'
              meta={{
                touched: false
              }}
              multiline
              variant='outlined'
            />
          </FeedbackPanel.InputWrapper>
          <FeedbackPanel.Title>
            Feedback Questions
          </FeedbackPanel.Title>
          {isLoadQuestion ? (
            <div style={{ textAlign: 'center' }}>
              <CircularProgress
                style={{ marginTop: 15, display: 'inline-block' }}
              />
            </div>
          ) : (
            <FeedbackPanel.Questions>
              {questions.map(({ question, taxingUnitPollQuestionID }, i) => (
                <FeedbackPanel.Question key={i}>
                  <FeedbackPanel.li i={i + 1}>{question}</FeedbackPanel.li>
                  <SelectInput
                    input={{
                      onBlur: () => {},
                      onChange: value =>
                        setResponses(v => ({
                          ...v,
                          [taxingUnitPollQuestionID]: value
                        })),
                      value: responses[taxingUnitPollQuestionID] || false
                    }}
                    options={questionOptions}
                    style={{ marginTop: -13, width: 100 }}
                  />
                </FeedbackPanel.Question>
              ))}
            </FeedbackPanel.Questions>
          )}
        </div>
        <FeedbackPanel.Contacts>
          <TextInput
            input={{
              onBlur: () => {},
              onChange: value => setPhone(value),
              value: phone
            }}
            label='Contact Phone Number'
            meta={{
              touched: false
            }}
            required
            type='tel'
          />
          <TextInput
            input={{
              onBlur: () => {},
              onChange: value => setEmail(value),
              value: email
            }}
            label='Contact Email'
            meta={{
              touched: false
            }}
            required
            type='email'
          />
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <span style={{ whiteSpace: 'nowrap', marginRight: 20 }}>
                Are you the owner of this property?
            </span>
            <SelectInput
              input={{
                onBlur: () => {},
                onChange: value => setIsOwner(value),
                value: isOwner
              }}
              options={[
                {
                  label: 'Yes',
                  value: 1
                },
                {
                  label: 'No',
                  value: 0
                }
              ]}
            />
          </div>
        </FeedbackPanel.Contacts>
        <FeedbackPanel.ButtonWrapper>
          <ReCAPTCHA
            sitekey={captchaKey}
            onChange={() => setCaptchaOk(true)}
          />
          <div style={{ marginLeft: 20 }}>
            <Button
              className={classes.button}
              disabled={isDisabled}
              type='submit'
              variant='contained'
            >
                SEND FEEDBACK
            </Button>
          </div>
        </FeedbackPanel.ButtonWrapper>
      </form>
    </div>
  );
};

FeedbackPanel.Title = styled.div`
  font-size: 15px;
  font-weight: bold;
  margin-bottom: 10px;
`;

FeedbackPanel.li = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  margin: 20px 0;
  word-break: break-all;

  ::before {
    content: '${props => props.i}.';
    position: absolute;
    margin-left: -20px;
    align-self: flex-start;
  }

  ::after {
    content: ' ';
    flex: 1 1 50px;
    background-image: linear-gradient(to right, #333 40%, rgba(255, 255, 255, 0) 20%);
    background-position: top;
    background-size: 10px 1px;
    background-repeat: repeat-x;
    display: block;
    height: 1px;
    margin: 0 15px;
  }
`;

FeedbackPanel.Question = styled.div`
  display: flex;
`;

FeedbackPanel.Questions = styled.div`
  padding: 0 20px;
`;

FeedbackPanel.InputWrapper = styled.div`
  fieldset {
    border-color: #333fb8 !important;
    background-color: rgb(239, 239, 255);
  }
`;

FeedbackPanel.Contacts = styled.div`
  display: flex;
  padding: 16px;
  align-items: flex-start;
  flex-wrap: wrap;

  > div {
    :last-child {
      margin-right: 20px;
    }

    flex-grow: 1;
    flex-shrink: 1;
    margin-right: 40px;
  }
`;

FeedbackPanel.ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

FeedbackPanel.propTypes = {
  captchaKey: PropTypes.string.isRequired,
  classes: PropTypes.objectOf(PropTypes.string),
  fetchTaxingUnitPollQuestions: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  pushToast: PropTypes.func.isRequired,
  unitId: PropTypes.oneOfType([ PropTypes.number, PropTypes.string ]).isRequired,
  year: PropTypes.string.isRequired
};

export default withStyles(styles)(FeedbackPanel);
